import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { 
  MessageSquare, 
  Users, 
  BookOpen,
  FileText,
  AlertTriangle,
  Scale,
  Home,
  Shield,
  Landmark,
  Search,
  Lightbulb,
  Briefcase,
  GraduationCap,
  Coffee,
  Crown,
  Lock,
  Send,
  Loader2
} from "lucide-react";
import { toast } from "sonner";

const iconMap = {
  BookOpen,
  FileText,
  AlertTriangle,
  Scale,
  Home,
  Shield,
  Landmark,
  Search,
  Lightbulb,
  Briefcase,
  GraduationCap,
  Coffee,
  MessageSquare
};

export default function CommunityPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Suggestion box state
  const [suggestion, setSuggestion] = useState("");
  const [suggestionEmail, setSuggestionEmail] = useState("");
  const [submittingSuggestion, setSubmittingSuggestion] = useState(false);

  useEffect(() => {
    if (user) {
      fetchCategories();
    }
  }, [user]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/forum/categories`);
      setCategories(response.data);
      setError(null);
    } catch (err) {
      if (err.response?.status === 403) {
        setError("subscription_required");
      } else {
        setError("failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitSuggestion = async (e) => {
    e.preventDefault();
    if (!suggestion.trim()) {
      toast.error("Please enter your suggestion");
      return;
    }
    
    setSubmittingSuggestion(true);
    try {
      await axios.post(`${API}/suggestions`, {
        suggestion: suggestion.trim(),
        email: suggestionEmail.trim() || (user?.email || "anonymous"),
        user_id: user?.user_id || null
      });
      toast.success("Thank you for your suggestion! We appreciate your feedback.");
      setSuggestion("");
      setSuggestionEmail("");
    } catch (err) {
      toast.error("Failed to submit suggestion. Please try again.");
    } finally {
      setSubmittingSuggestion(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen py-16 flex items-center justify-center">
        <Card className="max-w-md mx-4">
          <CardContent className="pt-6 text-center">
            <Lock className="h-12 w-12 text-gold mx-auto mb-4" />
            <h2 className="font-serif text-xl font-bold mb-2">Sign In Required</h2>
            <p className="text-muted-foreground mb-4">
              Please sign in to access the community forum.
            </p>
            <Button onClick={() => navigate("/login")} className="bg-gold text-primary-foreground">
              Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gold animate-pulse font-serif text-xl">Loading community...</div>
      </div>
    );
  }

  if (error === "subscription_required") {
    return (
      <div className="min-h-screen py-16 flex items-center justify-center">
        <Card className="max-w-md mx-4">
          <CardContent className="pt-6 text-center">
            <Users className="h-12 w-12 text-gold mx-auto mb-4" />
            <h2 className="font-serif text-xl font-bold mb-2">Subscriber Access Only</h2>
            <p className="text-muted-foreground mb-4">
              The community forum is available to subscribers. Join now to connect with other law students!
            </p>
            <Button onClick={() => navigate("/subscribe")} className="bg-gold text-primary-foreground">
              Subscribe Now
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Group categories by type
  const yearCategories = categories.filter(c => c.type === "year");
  const courseCategories = categories.filter(c => c.type === "course");
  const generalCategories = categories.filter(c => c.type === "general");

  const CategoryCard = ({ category }) => {
    const IconComponent = iconMap[category.icon] || MessageSquare;
    return (
      <Card 
        className="bg-card border-border/50 hover:border-gold/50 transition-all cursor-pointer group"
        onClick={() => navigate(`/community/${category.id}`)}
        data-testid={`category-${category.id}`}
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-gold/10 flex items-center justify-center flex-shrink-0">
              <IconComponent className="h-5 w-5 text-gold" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-medium group-hover:text-gold transition-colors">{category.name}</h3>
              <p className="text-xs text-muted-foreground line-clamp-1">{category.description}</p>
            </div>
            <Badge variant="outline" className="text-xs">
              {category.post_count} posts
            </Badge>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen py-8" data-testid="community-page">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Users className="h-8 w-8 text-gold" />
            <h1 className="font-serif text-3xl font-bold">Community Forum</h1>
          </div>
          <p className="text-muted-foreground">
            Connect with fellow law students, share study tips, and discuss courses
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-card/50">
            <CardContent className="p-4 text-center">
              <MessageSquare className="h-6 w-6 text-gold mx-auto mb-1" />
              <p className="text-2xl font-bold">{categories.reduce((sum, c) => sum + c.post_count, 0)}</p>
              <p className="text-xs text-muted-foreground">Total Posts</p>
            </CardContent>
          </Card>
          <Card className="bg-card/50">
            <CardContent className="p-4 text-center">
              <BookOpen className="h-6 w-6 text-gold mx-auto mb-1" />
              <p className="text-2xl font-bold">{categories.length}</p>
              <p className="text-xs text-muted-foreground">Categories</p>
            </CardContent>
          </Card>
          <Card className="bg-card/50 col-span-2 md:col-span-2">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Premium Members</p>
                  <p className="text-xs text-muted-foreground">Get access to direct messaging</p>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="border-gold text-gold"
                  onClick={() => navigate("/messages")}
                >
                  <Crown className="h-4 w-4 mr-1" />
                  Messages
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* By Law School Year */}
        <div className="mb-8">
          <h2 className="font-serif text-xl font-bold mb-4 flex items-center gap-2">
            <GraduationCap className="h-5 w-5 text-gold" />
            By Law School Year
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {yearCategories.map(cat => (
              <CategoryCard key={cat.id} category={cat} />
            ))}
          </div>
        </div>

        {/* By Course */}
        <div className="mb-8">
          <h2 className="font-serif text-xl font-bold mb-4 flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-gold" />
            By Course Subject
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {courseCategories.map(cat => (
              <CategoryCard key={cat.id} category={cat} />
            ))}
          </div>
        </div>

        {/* General Discussion */}
        <div className="mb-8">
          <h2 className="font-serif text-xl font-bold mb-4 flex items-center gap-2">
            <Coffee className="h-5 w-5 text-gold" />
            General Discussion
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {generalCategories.map(cat => (
              <CategoryCard key={cat.id} category={cat} />
            ))}
          </div>
        </div>

        {/* Suggestion Box */}
        <div className="mb-8">
          <Card className="bg-gradient-to-br from-gold/5 to-gold/10 border-gold/30">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gold/20 flex items-center justify-center">
                  <Lightbulb className="h-5 w-5 text-gold" />
                </div>
                <div>
                  <CardTitle className="font-serif text-xl">Suggestion Box</CardTitle>
                  <CardDescription>
                    Have an idea for a new topic, feature, or improvement? We'd love to hear from you!
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmitSuggestion} className="space-y-4">
                <Textarea
                  placeholder="Share your suggestion... (e.g., 'Please add a song for Evidence - Hearsay Exceptions')"
                  value={suggestion}
                  onChange={(e) => setSuggestion(e.target.value)}
                  className="min-h-[100px] bg-background/50 border-gold/30 focus:border-gold"
                  data-testid="suggestion-input"
                />
                <div className="flex flex-col sm:flex-row gap-3">
                  <Input
                    type="email"
                    placeholder="Your email (optional - for follow-up)"
                    value={suggestionEmail}
                    onChange={(e) => setSuggestionEmail(e.target.value)}
                    className="flex-1 bg-background/50 border-gold/30 focus:border-gold"
                    data-testid="suggestion-email"
                  />
                  <Button 
                    type="submit" 
                    className="bg-gold text-primary-foreground hover:bg-gold/90"
                    disabled={submittingSuggestion || !suggestion.trim()}
                    data-testid="submit-suggestion-btn"
                  >
                    {submittingSuggestion ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Submit Suggestion
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
